import React, { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ErrorBanner } from '@/components/common/ErrorBanner';
import { useLoginMutation } from '@/store/api/authApi';
import { useAppDispatch } from '@/hooks/redux';
import { setUserProfile } from '@/store/slices/authSlice';
import toast from 'react-hot-toast';
import { useLazyGetUserProfileQuery } from '@/store/api/userApi';
import { tokenService } from '@/utils/tokenService';

const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

type PageType = 'login' | 'register' | 'forgot-password';

type LoginFormData = z.infer<typeof loginSchema>;

interface RegisterProps {
  toggleAnimation: (newPage: PageType) => void;
}

export function Login({ toggleAnimation }: RegisterProps) {
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [login, { isLoading, error }] = useLoginMutation();
  const [fetchUser] = useLazyGetUserProfileQuery();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      const auth = await login(data).unwrap();

      tokenService.setAccessToken(auth.accessToken);
      if (auth.refreshToken) {
        tokenService.setRefreshToken(auth.refreshToken);
      }

      const user = await fetchUser().unwrap();
      dispatch(setUserProfile(user));

      toast.success('Login successful!');

      const redirectTo = (location.state as { redirectTo?: string } | null)?.redirectTo;
      navigate(redirectTo || '/dashboard');
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  return (
    <div className="space-y-5 w-full max-w-[420px] mx-auto">
      {error && (
        <ErrorBanner
          type="error"
          title="Login Failed"
          message={(error as any)?.data?.message || 'Invalid Email or password'}
        />
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <Input
            type="email"
            placeholder="Email"
            {...register('email')}
            className={`${errors.email ? 'border-destructive' : ''}`}
          />

          {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
        </div>

        <div className="space-y-2">
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              {...register('password')}
              className={`${errors.password ? 'border-destructive' : ''} pr-10`}
            />

            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 -translate-y-1/2"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>

          {errors.password && <p className="text-sm text-destructive">{errors.password.message}</p>}
        </div>

        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              toggleAnimation('forgot-password');
            }}
            className="text-sm text-primary hover:underline"
          >
            Forgot password?
          </button>
        </div>

        <Button type="submit" variant="auth" className="w-full">
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Sign In
        </Button>
      </form>

      <div className="text-center text-sm">
        <span className="text-muted-foreground">Don't have an account?</span>{' '}
        <button
          onClick={(e) => {
            e.preventDefault();
            toggleAnimation('register');
          }}
          className="text-primary hover:underline font-medium"
        >
          Sign up
        </button>
      </div>
    </div>
  );
}
